import { useCallback, useEffect, useState } from 'react';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useSiteUserPersonalization } from '@atlassian/jira-personalization-service/src/services/site-user/index.tsx';
import { useCurrentUser } from '@atlassian/jira-platform-services-user-current/src/main.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import {
	fetchCachedConfluenceAccess,
	setConfluenceAccessCache,
} from '../permission-check/index.tsx';
import {
	useIsInactiveConfluenceUser,
	useIsJiraAccountAgeEligible,
	useIsNewConfluenceUser,
} from '../traits/index.tsx';
/**
 * This suite of experiments has two potential groups of eligible users.
 *
 * Group 1: is used for all four touchpoints, and calculates eligibility based on the following checks:
 * - user has been active in Jira for at least 7 days
 * - user has been inactive in Confluence for at least 30 days
 * - user has permission to access Confluence
 *
 * Group 2: is used for the Jira search touchpoint only, and calculates eligibility based on the following checks:
 * - user has been active in Jira for at least 7 days
 * - user has never been activated in Confluence
 * - user has permission to access Confluence
 * @returns {object} containing three booleans, one for whether the user has opened the Confluence search panel,
 *  one for first group eligibility and one for second group eligibility
 */

export const useConfluenceReactivationExperiment = (cloudId: string, isSearchMenuOpen: boolean) => {
	const { user } = useCurrentUser().data;
	const { fetch } = useSiteUserPersonalization();
	const isAnonymousUser = useIsAnonymous();
	const [permitted, setPermitted] = useState<boolean>(false);
	const [nudgeShown, setNudgeShown] = useState<boolean>(false);

	const [passesJiraAgeCondition] = useIsJiraAccountAgeEligible();
	const [passesConfluenceInactivityCondition] = useIsInactiveConfluenceUser();
	const [passesNewConfluenceUserCondition] = useIsNewConfluenceUser();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const setAllowConfluenceAccessCache = useCallback(() => {
		setConfluenceAccessCache(true, createAnalyticsEvent);
	}, [createAnalyticsEvent]);

	useEffect(() => {
		const checkEligibility = async () => {
			if (!isSearchMenuOpen) {
				return;
			}

			if (isAnonymousUser) {
				return;
			}

			if (!passesJiraAgeCondition) {
				return;
			}

			if (!passesConfluenceInactivityCondition && !passesNewConfluenceUserCondition) {
				return;
			}

			try {
				const confluenceAccess = await fetchCachedConfluenceAccess(cloudId, createAnalyticsEvent);

				if (!confluenceAccess) {
					return;
				}
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: 'useConfluenceReactivationExperiment',
						packageName: 'jiraConfluenceReactivationExperiment',
					},
					error,
				});
			}

			if (
				!expValEquals(
					'cc_mandalorians_confluence_reactivation_ab',
					'cohort',
					'variation',
					'control',
				)
			) {
				return;
			}

			if (user.accountId) {
				const personalizationData = await fetch({ cloudId, userId: user.accountId });
				// We have no need for this data until we know the user is in the experiment.
				if (personalizationData) {
					const isClicked =
						personalizationData.find(
							(trait) =>
								trait.name ===
									'jira-platform_ui_panel_opened_crossproductsearchpanel_searchdialog_confluence_reactivation' &&
								trait.value === true,
						) !== undefined;
					setNudgeShown(isClicked);
				}
			}

			setPermitted(true);
		};

		checkEligibility();
	}, [
		cloudId,
		user,
		passesJiraAgeCondition,
		passesConfluenceInactivityCondition,
		passesNewConfluenceUserCondition,
		isSearchMenuOpen,
		isAnonymousUser,
		createAnalyticsEvent,
		fetch,
		setNudgeShown,
	]);

	const isEligibleInactiveUser =
		passesJiraAgeCondition && // jira_first_active + 7 < today
		passesConfluenceInactivityCondition && // should be inactive in Confluence for at least 30 days
		permitted; // should have confluence access

	const isEligibleNewUser =
		passesJiraAgeCondition && // jira_first_active + 7 < today
		passesNewConfluenceUserCondition && // should have never been active in Confluence
		permitted; // should have confluence access

	return {
		isEligibleInactiveUser,
		isEligibleNewUser,
		nudgeShown,
		setAllowConfluenceAccessCache,
	};
};
